import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../../components/Orchestrio";

const WebAppsPage = () => {
  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="webapps-service-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline mb-6 inline-block">
            ← Back to Services
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Web Applications
          </h1>
          <p className="text-xl text-[#5F8FB8] mb-4">
            Dashboards, portals, internal tools
          </p>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Designed for real users and long-term use.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">The problem we solve</h2>
          <div className="max-w-2xl">
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Your team is spending hours on manual processes that should be automated. 
              Data lives in spreadsheets. Critical workflows depend on email threads and memory.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Off-the-shelf software almost fits—but not quite. The workarounds are becoming 
              more complex than the original problem.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed">
              You need something built specifically for how your business actually works.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we approach it</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">We start with the workflow</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Before writing code, we map out exactly how people will use the system. 
                Who needs access to what? What's the actual process? Where are the pain points?
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We design for real users</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Interfaces that are clear and efficient, not cluttered with features nobody uses. 
                We test with actual users, not just stakeholders.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We build for change</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Requirements evolve. Good architecture makes changes possible without rewriting everything. 
                We plan for the features you'll need next year.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We document everything</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Technical documentation, user guides, architecture diagrams. 
                Your next developer—or your team—will understand exactly how it works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">What you can expect</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Deliverables</h3>
              <ul className="space-y-3">
                {[
                  "Requirements specification",
                  "UI/UX design (Figma)",
                  "Custom web application",
                  "User authentication & roles",
                  "Database design",
                  "API development",
                  "Admin interface",
                  "Testing & QA",
                  "Deployment to production",
                  "Technical documentation",
                  "User training session"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Timeline & Investment</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Typical timeline</p>
                  <p className="font-semibold">8–20 weeks</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Investment range</p>
                  <p className="font-semibold">£15,000 – £75,000+ +VAT</p>
                </div>
                <div className="pt-4 border-t border-[#2E2E2E]">
                  <p className="text-xs text-[#6B7280]">
                    Price depends on complexity, integrations, and user requirements. 
                    We provide a fixed quote after the discovery phase.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">What we've built</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { type: "Client Portal", desc: "Secure document access and matter tracking for a law firm" },
              { type: "Order System", desc: "Automated order processing with supplier integrations" },
              { type: "Booking Platform", desc: "Multi-location appointment scheduling with payments" }
            ].map((example, i) => (
              <div key={i} className="p-4 bg-[#1E1E1E] border border-[#2E2E2E]">
                <p className="font-semibold text-[#5F8FB8] text-sm mb-2">{example.type}</p>
                <p className="text-sm text-[#9CA3AF]">{example.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">This is right for you if...</h2>
          <ul className="space-y-3 max-w-2xl">
            {[
              "You're managing critical processes in spreadsheets or email",
              "Off-the-shelf software doesn't fit your workflow",
              "You need to give clients or partners controlled access to data",
              "You're ready to invest in custom software that you'll use for years"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                <span className="text-[#5F8FB8]">→</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Have a process that needs systematising?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch and walk us through what you're trying to solve. 
                We'll tell you honestly whether custom software is the right answer.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebAppsPage;
